import {Component, effect, Inject, Signal, TrackByFunction} from '@angular/core';
import {map, take} from 'rxjs/operators';
import {AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {BannerService} from '../../../banner/banner.service';
import {Apollo} from 'apollo-angular';
import {Airline, Mutation, StartWorkflowVodInput} from '../../../types-workflow';

import {START_WORKFLOW_VOD_DEFAULT} from '../../../queries-workflow';
import {forkJoin} from 'rxjs';
import {Router} from '@angular/router';
import {VodPassthroughFormatSpecificComponent} from '../vod-passthrough-format-specific/vod-passthrough-format-specific.component';
import {BasicPoitemVodInfoComponent} from '../basics-poitem-vod-info/basic-poitem-vod-info.component';
import {clean_datetime} from '../../utils';
import {poNumberValidator} from '../../name-limits';
import {PO_DESTINATIONS, PoDestination} from '../../../target_systems';
import {Organization} from '../../../types-repository';
import {Po2024Service} from '../../../services/po-2024.service';

import {reportInvalid} from '../form-messages';
import {DoButtonComponent} from '../../do-button/do-button.component';
import {FormFooterComponent} from '../../form-footer/form-footer.component';
import {ContextService} from '../../../services/context.service';
import {ProcessService} from '../../../services/process.service';
import {Dialog} from '@angular/cdk/dialog';
import {ConfirmDialogComponent} from '../../../confirm-dialog/confirm-dialog.component';


/** The `po_name` this form submits — the single place it is composed. */
function composePoName(form: AbstractControl): string {
  return `${form.get('airline')?.value ?? ''}_${(form.get('po_number')?.value ?? '').trim()}`;
}

@Component({
  selector: 'app-vod-passthrough-po',
  templateUrl: './vod-passthrough-po.component.html',
  styleUrls: ['./vod-passthrough-po.component.css'],
  imports: [
    ReactiveFormsModule,
    BasicPoitemVodInfoComponent,
    VodPassthroughFormatSpecificComponent,
    FormFooterComponent
]
})
/**
 * Submit form for watermark-only passthrough delivery (`vod-default-passthrough-sffw`).
 *
 * Structurally identical to {@link VodDefaultPoComponent} -- same airline / project / destination
 * header, same per-item list fed from the folder selection -- and it calls the very same
 * `start_workflow_vod_default` mutation. Only the template name and the format-specific block differ:
 * the job re-encodes the video essence solely to carry the Stardust mark and passes container, codec
 * and every non-video stream through untouched, so there is no encryption, thumbnail or renditions
 * option to offer here.
 *
 * The tab this lives in is gated on `singleframewatermarking:Demo` (policy "STARDUSTmark: Demo"),
 * while the mutation itself is authorized as `StartWorkflowVodDefault` like any other VOD encode.
 */
export class VodPassthroughPoComponent  {

  organization: Signal<Organization>;
  airlines: Signal<Airline[]>;
  poDestinations: PoDestination[] = []
  /**
   * Prefix of every element id in this form. Both VOD forms sit side by side in the folder page's
   * tabs, and ids are looked up document-wide (reportInvalid's focus scope, labels): with shared
   * ids a refused STARDUSTmark submit went to the VOD Default item and switched to its tab.
   */
  readonly formId = 'vod-passthrough-po';

  poFormGroup: FormGroup;

  poItemTrackBy: TrackByFunction<FormGroup> = (index: number, poitem: FormGroup) => {
    return poitem.get('id').value;
  }

  constructor(
    private apollo: Apollo,
    @Inject('vod_passthrough_sffw') protected poService: Po2024Service,
    private formBuilder: FormBuilder,
    private banner: BannerService,
    contextService: ContextService,
    private processService: ProcessService,
    private router: Router,
    private dialog: Dialog
  ) {
    this.organization = contextService.organization;
    this.airlines = contextService.airlines;

    this.poFormGroup = formBuilder.group({
      airline: [null, Validators.required],
      po_number: [clean_datetime(new Date()), [Validators.required, poNumberValidator(composePoName)]],
      po_destination: [null, Validators.required],
      auto_publish: [true, Validators.required],
      poItems: formBuilder.array([]),
    });


    // The Project ID's limits are on the composed po_name, which also carries the airline.
    this.poFormGroup.controls['airline'].valueChanges.subscribe(() => {
      this.poFormGroup.controls['po_number'].updateValueAndValidity();
    });
    this.poService.getFormState().pipe(take(1)).subscribe(savedForm => {
      if (savedForm) {
        savedForm.poItems.forEach(poItem => (this.poFormGroup.controls.poItems as FormArray).push(this.formBuilder.group(poItem)));
        this.poFormGroup.patchValue(savedForm, {emitEvent: false})
      }
    });
    effect(() => {
      this.poFormGroup.controls['airline'].valueChanges.subscribe(airline => {
        this.poDestinations = PO_DESTINATIONS.hasOwnProperty(this.organization().legacy_name)
          ? [{display_name: this.organization().legacy_name, destination_id: this.organization().legacy_name}, {
            display_name: 'Default CDN',
            destination_id: 'vod'
          }]
          : [{display_name: 'Default CDN', destination_id: 'vod'}];
        this.poFormGroup.controls['po_destination'].setValue(this.poDestinations[0].destination_id)
      })
    });
    this.poService.poItemToAdd$.subscribe(data => {
      console.log('poItemToAdd$')
      if (data) {
        (this.poFormGroup.controls.poItems as FormArray).push(this.formBuilder.group(
          Object.assign(
            {},
            {
              basics: BasicPoitemVodInfoComponent.DEFAULTS,
              format_specific_data: VodPassthroughFormatSpecificComponent.DEFAULTS
            },
            data)))
      }
    });
    this.poService.poItemToRemove$.subscribe(id => {
      if (id) {
        const index = (this.poFormGroup.controls.poItems as FormArray).controls.findIndex(poItem => poItem.get('id').value === id);
        (this.poFormGroup.controls.poItems as FormArray).removeAt(index);
      }
    });
    effect(() => {
      if (this.airlines().length === 1) {
        this.poFormGroup.controls['airline'].setValue(this.airlines()[0].iata_code);
      }
    })
  }



  compare_destination_id(o1: any, o2: any) {
    console.log(o1, o2)
    return o1?.destination_id === o2?.destination_id;
  }

  submitted = false;

  submit_po(button: DoButtonComponent) {
    const organization = this.organization()
    if (!this.poFormGroup.valid) {
      this.submitted = true;
      const poItems = this.poFormGroup.controls.poItems as FormArray
      const invalidIdx = poItems.controls.findIndex(c => c.invalid)
      this.poFormGroup.markAllAsTouched();
      // Scroll to and focus the first invalid field of this form only (its ids are unique per form).
      reportInvalid(button, this.poFormGroup, invalidIdx >= 0 ? `${this.formId}-item-${invalidIdx}` : this.formId);
      return
    }
    const workflows = []
    for (const poItem of this.poFormGroup.value.poItems) {
      workflows.push(this.apollo.use('workflow').mutate<Mutation>({
        mutation: START_WORKFLOW_VOD_DEFAULT,
        variables: {
          input_brefix: poItem.basics.input_brefix,
          po_item_id: poItem.basics.po_item_id.trim(),
          po_name: composePoName(this.poFormGroup),
          po_destination: this.poFormGroup.value.po_destination,
          vtk_template: 'vod-default-passthrough-sffw',
          auto_publish: this.poFormGroup.value.auto_publish,
          format_specific_data: JSON.stringify(VodPassthroughFormatSpecificComponent.forSubmit(poItem.format_specific_data))
        } satisfies StartWorkflowVodInput

      }).pipe(
        map(d => ({
          process: d.data.start_workflow_vod_default,
          po_item_id: poItem.basics.po_item_id.trim()
        }))))
    }

    forkJoin(workflows).subscribe(
      {
        next: wfs => {
          button.done('');

          const ref = this.dialog.open(ConfirmDialogComponent, {
            data: {
              headline: 'Watermarking started',
              message: `All watermark job${wfs.length === 1 ? '' : 's'} (${wfs.length} item${wfs.length === 1 ? '' : 's'}) started successfully.`,
            }
          });

          ref.closed.subscribe(() => {
            this.submitted = false;
            (this.poFormGroup.controls.poItems as FormArray).clear();
            this.poFormGroup.reset({
              airline: this.airlines().length === 1 ? this.airlines()[0].iata_code : null,
              po_number: clean_datetime(new Date()),
              po_destination: this.poFormGroup.value.po_destination,
              auto_publish: true,
              poItems: [],
            });
          });

          wfs.forEach(workflow => {
            this.processService.observeProcess(workflow.process, {
              successMessage: `${workflow.po_item_id} watermarked.`,
              failureMessage: `Failed to watermark ${workflow.po_item_id}.`,
              link: ['/#/o', organization.id, 'workflows',
                this.poFormGroup.value.airline,
                `${this.poFormGroup.value.airline}_${this.poFormGroup.value.po_number}`].join('/')
            })
          })
        },
        error: err => {
          button.done('Workflow submission failed: ' + err, {severity: 'cl-alert-danger'});
        }
      }
    )

  }

  get poItems() {
    return this.poFormGroup.get('poItems') as FormArray<FormGroup>;
  }

  ngOnDestroy(): void {
    this.poService.setFormState(this.poFormGroup.value)
  }

}
